import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type AdminScreen, navigableScreensFor } from '../api.js';
import { consoleSectionsOf, NAV_GROUPS, SCREEN_REGISTRY, type NavSectionId } from '../screens/registry.js';
import type { AdminSession, Tenant, ToastMessage } from '../types.js';
import { TabStrip } from './TabStrip.js';

export function AppShell({
  session,
  screen,
  toast,
  tenants,
  selectedTenantId,
  onScreen,
  onTenant,
  onDismissToast,
  onLogout,
  children
}: {
  session: AdminSession;
  screen: AdminScreen;
  toast?: ToastMessage & { id?: number };
  tenants?: Tenant[];
  selectedTenantId?: string;
  onScreen: (screen: AdminScreen) => void;
  onTenant?: (tenantId: string) => void;
  onDismissToast?: () => void;
  onLogout: () => void;
  children: React.ReactNode;
}) {
  const [navOpen, setNavOpen] = useState(false);
  const [mobileNav, setMobileNav] = useState(false);
  const navToggleRef = useRef<HTMLButtonElement>(null);
  const sidebarRef = useRef<HTMLElement>(null);
  const wasNavOpen = useRef(false);
  const { i18n, t } = useTranslation();
  const roles = session.bootstrap.membership.roles;
  const visible = navigableScreensFor(roles);
  // Platform AND partner admins are cross-tenant operators: they act INTO
  // tenants, so both get the tenant selector + act-as banner.
  const isCrossTenantOperator = roles.includes('platform_admin') || roles.includes('partner_admin');
  // Group the role-visible screens for the sidebar. Visibility itself is
  // still decided by navigableScreensFor — the registry only adds grouping.
  const groups = NAV_GROUPS.map((group) => ({
    ...group,
    items: SCREEN_REGISTRY.filter((def) => def.group === group.id && visible.includes(def.id))
  })).filter((group) => group.items.length > 0);

  // A registry `section` is a console: the sidebar shows one entry for it and
  // its screens become the tabs. So the tab strip is just another way to call
  // onScreen — screens, ids, role mapping, and per-screen toasts stay as they
  // are, and only the active screen is ever mounted.
  const activeSection = SCREEN_REGISTRY.find((def) => def.id === screen)?.section;
  const consoleTabs = activeSection
    ? SCREEN_REGISTRY.filter((def) => def.section === activeSection && visible.includes(def.id))
    : [];
  const consoleLabel = activeSection ? t(`nav.${activeSection}`, activeSection) : undefined;

  useEffect(() => {
    const query = window.matchMedia('(max-width: 1024px)');
    const update = () => {
      setMobileNav(query.matches);
      if (!query.matches) setNavOpen(false);
    };
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    if (!mobileNav) return;
    if (navOpen) sidebarRef.current?.querySelector<HTMLElement>('button')?.focus();
    else if (wasNavOpen.current) navToggleRef.current?.focus();
    wasNavOpen.current = navOpen;
  }, [mobileNav, navOpen]);

  useEffect(() => {
    if (!navOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setNavOpen(false);
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [navOpen]);

  const selectScreen = (nextScreen: AdminScreen) => {
    onScreen(nextScreen);
    setNavOpen(false);
  };

  const navItem = (def: (typeof SCREEN_REGISTRY)[number]) => (
    <button
      key={def.id}
      className={def.id === screen ? 'nav-item active' : 'nav-item'}
      aria-current={def.id === screen ? 'page' : undefined}
      onClick={() => selectScreen(def.id)}
    >
      {t(`nav.${def.id}`, def.label)}
    </button>
  );

  // A console entry opens its first role-visible screen; the tab strip takes
  // over from there.
  const consoleItem = (section: NavSectionId, items: (typeof SCREEN_REGISTRY)[number][]) => (
    <button
      key={section}
      className={section === activeSection ? 'nav-item active' : 'nav-item'}
      aria-current={section === activeSection ? 'page' : undefined}
      onClick={() => { if (items[0]) selectScreen(items[0].id); }}
    >
      {t(`nav.${section}`, section)}
    </button>
  );

  return (
    <div className="app-shell">
      <aside
        ref={sidebarRef}
        className={navOpen ? 'sidebar open' : 'sidebar'}
        aria-hidden={mobileNav && !navOpen}
        {...(mobileNav && !navOpen ? { inert: '' } : {})}
      >
        <div>
          <div className="brand">Voost AI</div>
          <div className="sidebar-caption">{t('appShell.adminConsole')}</div>
        </div>
        <nav aria-label={t('appShell.adminScreens')}>
          {groups.map((group) => (
            <div className="nav-group" key={group.id} role="group" aria-label={t(`nav.${group.id}`, group.label)}>
              {groups.length > 1 ? <div className="nav-group-label">{t(`nav.${group.id}`, group.label)}</div> : null}
              {consoleSectionsOf(group.items).map((section) =>
                consoleItem(section, group.items.filter((def) => def.section === section))
              )}
              {group.items.filter((def) => !def.section).map(navItem)}
            </div>
          ))}
        </nav>
        <div className="account">
          <span>{session.bootstrap.user.email}</span>
          <label className="language-switcher">
            <span>{t('language.label')}</span>
            <select value={i18n.language.startsWith('en') ? 'en' : 'ko'} onChange={(event) => void i18n.changeLanguage(event.target.value)}>
              <option value="ko">{t('language.ko')}</option>
              <option value="en">{t('language.en')}</option>
            </select>
          </label>
          <button className="secondary" onClick={onLogout}>
            {t('appShell.signOut')}
          </button>
        </div>
      </aside>
      {navOpen ? <div className="nav-backdrop" onClick={() => setNavOpen(false)} /> : null}
      <main className="main">
        <header className="topbar">
          <div className="topbar-title">
            <button
              ref={navToggleRef}
              type="button"
              className="nav-toggle secondary"
              aria-label={t('common.menu')}
              aria-expanded={navOpen}
              onClick={() => setNavOpen((current) => !current)}
            >
              ☰
            </button>
            {/* Show the tenant the screens actually operate on — during an
                act-as session that is the impersonated tenant, not home. */}
            <div>
              <div className="eyebrow">
                {isCrossTenantOperator && session.tenantContext
                  ? session.tenantContext.tenantName
                  : session.bootstrap.tenant.name}
              </div>
              {/* Inside a console the tab already names the screen, so the
                  title names the console — same reading order as the tab strip
                  below it. With no strip (a role that can reach one screen of
                  the console) the title must name the screen, or its name
                  appears nowhere. */}
              <h1>{(consoleTabs.length > 1 ? consoleLabel : undefined) ?? t(`nav.${screen}`, screen)}</h1>
            </div>
          </div>
          {isCrossTenantOperator && onTenant ? (
            <label className="tenant-context">
              {t('appShell.tenantContext')}
              <select
                className={session.tenantContext ? 'active' : undefined}
                value={selectedTenantId ?? ''}
                onChange={(event) => onTenant(event.target.value)}
              >
                <option value="">{t('appShell.selectTenant')}</option>
                {(tenants ?? []).map((tenant) => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.name} ({tenant.id})
                  </option>
                ))}
              </select>
            </label>
          ) : null}
        {isCrossTenantOperator && session.tenantContext ? (
          <div className="context-banner compact" role="status">
            <span>
              <strong>{t('appShell.actingAsTenant')}</strong> {session.tenantContext.tenantName}{' '}
              <code>{session.tenantContext.tenantId}</code>
            </span>
            {onTenant ? (
              <button type="button" className="secondary" onClick={() => onTenant('')}>
                {t('appShell.exitTenantContext')}
              </button>
            ) : null}
          </div>
        ) : null}
        </header>
        {toast ? (
          <div className="toast-region">
            <div key={toast.id} className={`toast ${toast.tone}`} role={toast.tone === 'error' ? 'alert' : 'status'}>
              <span>{toast.message}</span>
              {onDismissToast ? (
                <button type="button" className="toast-close" aria-label={t('common.dismiss')} onClick={onDismissToast}>
                  x
                </button>
              ) : null}
            </div>
          </div>
        ) : null}
        {/* A console with one visible screen (a narrow role) gets no strip —
            the topbar title already names it. */}
        {consoleTabs.length > 1 ? (
          <TabStrip
            tabs={consoleTabs.map((def) => ({ id: def.id, label: t(`nav.${def.id}`, def.label) }))}
            value={screen}
            onChange={onScreen}
            {...(consoleLabel ? { ariaLabel: consoleLabel } : {})}
          />
        ) : null}
        {children}
      </main>
    </div>
  );
}
